import React, { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;

// Crosshair marker icon with glow on hover
const createCrosshairIcon = (isMain = false, isHovered = false) => {
  const size = isMain ? 40 : 28;
  const color = isMain ? '#0ea5e9' : '#22d3ee';
  const glowIntensity = isHovered ? '0 0 20px #0ea5e9, 0 0 40px #0ea5e9, 0 0 60px #0ea5e9' : '0 0 10px rgba(14, 165, 233, 0.5)';
  
  return L.divIcon({
    className: 'crosshair-marker',
    html: `
      <div class="crosshair-container" style="
        width: ${size}px;
        height: ${size}px;
        position: relative;
        transition: all 0.3s ease;
        filter: drop-shadow(${glowIntensity});
      ">
        <!-- Outer circle -->
        <svg width="${size}" height="${size}" viewBox="0 0 40 40" style="position: absolute; top: 0; left: 0;">
          <!-- Outer ring -->
          <circle cx="20" cy="20" r="18" fill="none" stroke="${color}" stroke-width="2" opacity="0.8"/>
          <!-- Inner ring -->
          <circle cx="20" cy="20" r="12" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.6"/>
          <!-- Center dot -->
          <circle cx="20" cy="20" r="4" fill="${color}" opacity="0.9"/>
          <!-- Crosshair lines -->
          <line x1="20" y1="2" x2="20" y2="8" stroke="${color}" stroke-width="2" opacity="0.8"/>
          <line x1="20" y1="32" x2="20" y2="38" stroke="${color}" stroke-width="2" opacity="0.8"/>
          <line x1="2" y1="20" x2="8" y2="20" stroke="${color}" stroke-width="2" opacity="0.8"/>
          <line x1="32" y1="20" x2="38" y2="20" stroke="${color}" stroke-width="2" opacity="0.8"/>
          ${isMain ? `
            <!-- Extra decorative elements for main marker -->
            <circle cx="20" cy="20" r="6" fill="none" stroke="${color}" stroke-width="1" stroke-dasharray="2,2" opacity="0.5">
              <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="10s" repeatCount="indefinite"/>
            </circle>
          ` : ''}
        </svg>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2],
  });
};

// Radar ping animation component
const RadarPing = ({ center }) => {
  const [rings, setRings] = useState([]);
  
  useEffect(() => {
    // Create new ring every 2 seconds
    const interval = setInterval(() => {
      const id = Date.now();
      setRings(prev => [...prev, { id, radius: 0, opacity: 0.6 }]);
      
      // Remove ring after animation completes
      setTimeout(() => {
        setRings(prev => prev.filter(ring => ring.id !== id));
      }, 2000);
    }, 2000);
    
    // Start with one ring immediately
    const initialId = Date.now();
    setRings([{ id: initialId, radius: 0, opacity: 0.6 }]);
    setTimeout(() => {
      setRings(prev => prev.filter(ring => ring.id !== initialId));
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <>
      {rings.map(ring => (
        <AnimatedRing key={ring.id} center={center} />
      ))}
    </>
  );
};

// Single animated ring
const AnimatedRing = ({ center }) => {
  const [radius, setRadius] = useState(1000);
  const [opacity, setOpacity] = useState(0.5);
  const maxRadius = 50000; // 50km
  
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const radiusIncrement = maxRadius / steps;
    const opacityDecrement = 0.5 / steps;
    let step = 0;
    
    const timer = setInterval(() => {
      if (step < steps) {
        setRadius(prev => prev + radiusIncrement);
        setOpacity(prev => Math.max(0, prev - opacityDecrement));
        step++;
      } else {
        clearInterval(timer);
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, []);
  
  return (
    <Circle
      center={center}
      radius={radius}
      pathOptions={{
        color: '#22d3ee',
        fillColor: 'transparent',
        fillOpacity: 0,
        weight: 3,
        opacity: opacity,
      }}
    />
  );
};

// Static coverage zone circle
const CoverageZone = ({ center, radius }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <>
      {/* Main coverage area */}
      <Circle
        center={center}
        radius={radius}
        pathOptions={{
          color: '#0ea5e9',
          fillColor: '#0ea5e9',
          fillOpacity: isHovered ? 0.15 : 0.08,
          weight: 2,
          opacity: isHovered ? 0.8 : 0.5,
          dashArray: isHovered ? '' : '10, 5',
        }}
        eventHandlers={{
          mouseover: () => setIsHovered(true),
          mouseout: () => setIsHovered(false),
        }}
      />
      {/* Inner zone */}
      <Circle
        center={center}
        radius={radius * 0.5}
        pathOptions={{
          color: '#22d3ee',
          fillColor: '#22d3ee',
          fillOpacity: 0.05,
          weight: 1,
          opacity: 0.3,
          dashArray: '5, 10',
        }}
      />
    </>
  );
};

// Map controller
const MapController = () => {
  const map = useMap();
  
  useEffect(() => {
    map.setMinZoom(8);
    map.setMaxZoom(12);
    
    const bounds = L.latLngBounds(
      L.latLng(45.8, 25.8),
      L.latLng(47.0, 27.6)
    );
    map.setMaxBounds(bounds);
  }, [map]);
  
  return null;
};

// Hoverable marker component
const HoverableMarker = ({ position, isMain, name, description }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <Marker
      position={position}
      icon={createCrosshairIcon(isMain, isHovered)}
      eventHandlers={{
        mouseover: () => setIsHovered(true),
        mouseout: () => setIsHovered(false),
      }}
    >
      <Popup>
        <div className="text-center">
          <h4 className={`font-bold text-base mb-1 ${isMain ? 'text-cyan-400' : 'text-cyan-300'}`}>
            {name}
          </h4>
          <p className="text-slate-300 text-sm">{description}</p>
          {isMain && (
            <p className="text-cyan-400 text-xs mt-2 font-semibold">SEDIU CENTRAL - ACTIV 24/7</p>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

const MapSection = () => {
  const bacauCenter = [46.5670, 26.9146];
  const radius50km = 50000;
  
  const coverageLocations = [
    { name: 'BACĂU HQ', coords: [46.5670, 26.9146], main: true, description: 'Bulevardul Unirii, Nr. 20' },
    { name: 'Onești', coords: [46.2474, 26.7580], description: 'Zonă monitorizată' },
    { name: 'Moinești', coords: [46.4333, 26.4833], description: 'Zonă monitorizată' },
    { name: 'Buhuși', coords: [46.7167, 26.7000], description: 'Zonă monitorizată' },
    { name: 'Comănești', coords: [46.4264, 26.4361], description: 'Zonă monitorizată' },
    { name: 'Dărmănești', coords: [46.3667, 26.4667], description: 'Zonă monitorizată' },
    { name: 'Slănic-Moldova', coords: [46.2000, 26.4333], description: 'Zonă monitorizată' },
    { name: 'Târgu Ocna', coords: [46.2833, 26.6167], description: 'Zonă monitorizată' },
  ];
  
  const coverageAreas = ['Onești', 'Moinești', 'Buhuși', 'Comănești', 'Dărmănești', 'Slănic-Moldova', 'Târgu Ocna'];
  
  return (
    <section className="py-16 bg-slate-800">
      <style>{`
        .crosshair-marker {
          background: transparent !important;
          border: none !important;
        }
        
        .crosshair-marker:hover .crosshair-container {
          filter: drop-shadow(0 0 20px #0ea5e9) drop-shadow(0 0 40px #0ea5e9) !important;
          transform: scale(1.1);
        }
        
        .leaflet-container {
          background: #1a365d !important;
        }
        
        .leaflet-popup-content-wrapper {
          background: rgba(15, 23, 42, 0.95) !important;
          color: white !important;
          border: 1px solid #0ea5e9 !important;
          border-radius: 12px !important;
          box-shadow: 0 0 20px rgba(14, 165, 233, 0.3) !important;
        }
        
        .leaflet-popup-tip {
          background: rgba(15, 23, 42, 0.95) !important;
          border-color: #0ea5e9 !important;
        }
        
        .leaflet-popup-content {
          margin: 12px 16px !important;
        }
        
        .leaflet-control-zoom a {
          background: rgba(15, 23, 42, 0.9) !important;
          color: #0ea5e9 !important;
          border-color: #0ea5e9 !important;
        }
        
        .leaflet-control-zoom a:hover {
          background: rgba(14, 165, 233, 0.2) !important;
          color: #22d3ee !important;
        }
        
        .leaflet-control-attribution {
          background: rgba(15, 23, 42, 0.8) !important;
          color: #64748b !important;
        }
        
        .leaflet-control-attribution a {
          color: #0ea5e9 !important;
        }
        
        /* Blue overlay effect */
        .map-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            rgba(14, 165, 233, 0.08) 0%,
            rgba(34, 211, 238, 0.05) 50%,
            rgba(14, 165, 233, 0.08) 100%
          );
          pointer-events: none;
          z-index: 1000;
          mix-blend-mode: screen;
        }
        
        /* Scan line effect */
        .scan-lines {
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(14, 165, 233, 0.03) 2px,
            rgba(14, 165, 233, 0.03) 4px
          );
          pointer-events: none;
          z-index: 1001;
        }
        
        /* Corner brackets */
        .corner-bracket {
          position: absolute;
          width: 30px;
          height: 30px;
          border-color: #0ea5e9;
          opacity: 0.6;
          z-index: 1002;
        }
        
        .corner-tl { top: 10px; left: 10px; border-top: 3px solid; border-left: 3px solid; }
        .corner-tr { top: 10px; right: 10px; border-top: 3px solid; border-right: 3px solid; }
        .corner-bl { bottom: 10px; left: 10px; border-bottom: 3px solid; border-left: 3px solid; }
        .corner-br { bottom: 10px; right: 10px; border-bottom: 3px solid; border-right: 3px solid; }
        
        @keyframes pulse-status {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .status-pulse {
          animation: pulse-status 2s ease-in-out infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="inline-block text-cyan-400 font-semibold text-sm uppercase tracking-wider mb-4">
            Sistem de Monitorizare
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Zone Acoperite de Detectivii Bacău
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Rază de acțiune: 50km • Monitorizare activă în tot județul Bacău
          </p>
        </div>

        {/* Map Container */}
        <div className="relative rounded-2xl overflow-hidden border-2 border-cyan-500/30 shadow-2xl shadow-cyan-500/10">
          {/* Blue overlay for digital look */}
          <div className="map-overlay" />
          
          {/* Scan lines effect */}
          <div className="scan-lines" />
          
          {/* Corner brackets */}
          <div className="corner-bracket corner-tl" />
          <div className="corner-bracket corner-tr" />
          <div className="corner-bracket corner-bl" />
          <div className="corner-bracket corner-br" />
          
          <MapContainer
            center={bacauCenter}
            zoom={9}
            style={{ height: '500px', width: '100%' }}
            zoomControl={true}
            scrollWheelZoom={true}
          >
            {/* Google Maps Standard style - OpenStreetMap */}
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              className="map-tiles"
            />
            
            <MapController />
            
            {/* Static coverage zone */}
            <CoverageZone center={bacauCenter} radius={radius50km} />
            
            {/* Radar ping animation */}
            <RadarPing center={bacauCenter} />
            
            {/* Crosshair markers */}
            {coverageLocations.map((location) => (
              <HoverableMarker
                key={location.name}
                position={location.coords}
                isMain={location.main}
                name={location.name}
                description={location.description}
              />
            ))}
          </MapContainer>
          
          {/* Status overlay - top left */}
          <div className="absolute top-4 left-4 bg-slate-900/90 backdrop-blur-sm border border-cyan-500/30 rounded-lg px-4 py-3 z-[1000]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-cyan-400 rounded-full status-pulse" />
              <span className="text-cyan-400 text-xs font-bold uppercase tracking-wider">Sistem Activ</span>
            </div>
            <div className="text-slate-400 text-xs">
              <div>Rază: 50km</div>
              <div>Puncte: {coverageLocations.length}</div>
            </div>
          </div>
          
          {/* Legend - top right */}
          <div className="absolute top-4 right-4 bg-slate-900/90 backdrop-blur-sm border border-cyan-500/30 rounded-lg px-4 py-3 z-[1000]">
            <h4 className="text-cyan-400 text-xs font-bold uppercase tracking-wider mb-3">Legendă</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="relative w-5 h-5">
                  <svg viewBox="0 0 20 20" className="w-full h-full">
                    <circle cx="10" cy="10" r="8" fill="none" stroke="#0ea5e9" strokeWidth="1.5"/>
                    <circle cx="10" cy="10" r="2" fill="#0ea5e9"/>
                    <line x1="10" y1="1" x2="10" y2="4" stroke="#0ea5e9" strokeWidth="1.5"/>
                    <line x1="10" y1="16" x2="10" y2="19" stroke="#0ea5e9" strokeWidth="1.5"/>
                    <line x1="1" y1="10" x2="4" y2="10" stroke="#0ea5e9" strokeWidth="1.5"/>
                    <line x1="16" y1="10" x2="19" y2="10" stroke="#0ea5e9" strokeWidth="1.5"/>
                  </svg>
                </div>
                <span className="text-slate-300 text-xs">Sediu central</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative w-5 h-5">
                  <svg viewBox="0 0 20 20" className="w-full h-full">
                    <circle cx="10" cy="10" r="6" fill="none" stroke="#22d3ee" strokeWidth="1"/>
                    <circle cx="10" cy="10" r="1.5" fill="#22d3ee"/>
                    <line x1="10" y1="2" x2="10" y2="5" stroke="#22d3ee" strokeWidth="1"/>
                    <line x1="10" y1="15" x2="10" y2="18" stroke="#22d3ee" strokeWidth="1"/>
                    <line x1="2" y1="10" x2="5" y2="10" stroke="#22d3ee" strokeWidth="1"/>
                    <line x1="15" y1="10" x2="18" y2="10" stroke="#22d3ee" strokeWidth="1"/>
                  </svg>
                </div>
                <span className="text-slate-300 text-xs">Zonă monitorizată</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-cyan-400/50 rounded-full relative">
                  <div className="absolute inset-1 border border-cyan-400/30 rounded-full animate-ping" />
                </div>
                <span className="text-slate-300 text-xs">Scanare activă</span>
              </div>
            </div>
          </div>
        </div>

        {/* Coverage Areas Grid */}
        <div className="mt-10">
          <h3 className="text-center text-lg font-semibold text-white mb-6">
            Localități Monitorizate
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {/* Main city */}
            <div className="bg-cyan-500/20 border border-cyan-500/40 rounded-full px-5 py-2 flex items-center gap-2 shadow-lg shadow-cyan-500/10">
              <div className="relative">
                <MapPin className="w-4 h-4 text-cyan-400" />
                <div className="absolute inset-0 animate-ping">
                  <MapPin className="w-4 h-4 text-cyan-400 opacity-50" />
                </div>
              </div>
              <span className="text-cyan-400 font-semibold text-sm">Bacău HQ</span>
            </div>
            
            {/* Other areas */}
            {coverageAreas.map((area) => (
              <div 
                key={area}
                className="bg-slate-700/50 border border-slate-600/50 rounded-full px-4 py-2 flex items-center gap-2 hover:bg-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 group"
              >
                <div className="w-2 h-2 bg-cyan-400 rounded-full group-hover:shadow-[0_0_8px_#22d3ee] transition-shadow" />
                <span className="text-slate-300 text-sm group-hover:text-cyan-300 transition-colors">{area}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-slate-500 text-sm mt-6">
            + alte localități din județul Bacău la cerere
          </p>
        </div>
      </div>
    </section>
  );
};

export default MapSection;
